import React, { useMemo, useState } from 'react';
import { supabase } from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import AdminStripeTaxMappingsPanel from './AdminStripeTaxMappingsPanel';
import AdminDirectChargeRolloutPanel from './AdminDirectChargeRolloutPanel';
import AdminDirectChargeFinancePanel from './AdminDirectChargeFinancePanel';
import * as FiIcons from 'react-icons/fi';

const {
  FiActivity,
  FiAlertTriangle,
  FiBarChart2,
  FiCreditCard,
  FiDownload,
  FiDollarSign,
  FiFileText,
  FiPercent,
  FiRefreshCw,
  FiSettings,
  FiShield,
  FiShoppingBag,
  FiTruck,
  FiUsers
} = FiIcons;

const money = (value) => `$${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})}`;

const csvCell = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;

const downloadCsv = (name, rows) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.map(csvCell).join(','),
    ...rows.map((row) => headers.map((header) => csvCell(row[header])).join(','))
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
};

const metricCards = [
  ['gross_order_volume', 'Gross order volume', FiActivity],
  ['product_subtotal', 'Product subtotal', FiShoppingBag],
  ['taxes_collected', 'Taxes collected', FiPercent],
  ['platform_fees', 'SOHLOCAL fees', FiDollarSign],
  ['stripe_fees', 'Stripe fees', FiCreditCard],
  ['seller_payouts', 'Seller payouts', FiUsers],
  ['refunds', 'Refunds / Disputes', FiRefreshCw],
  ['pending_payouts', 'Pending payouts', FiTruck],
  ['failed_payments', 'Failed payments', FiAlertTriangle],
  ['net_sohlocal_revenue', 'Net SOHLocal revenue', FiBarChart2]
];

const tabs = [
  ['overview', 'Overview', FiActivity],
  ['transactions', 'Transactions', FiFileText],
  ['payouts', 'Seller Payouts', FiUsers],
  ['taxes', 'Taxes', FiPercent],
  ['refunds', 'Refunds / Disputes', FiRefreshCw],
  ['revenue', 'Platform Revenue', FiDollarSign],
  ['exports', 'Reports / Exports', FiDownload],
  ['settings', 'Settings', FiSettings],
  ['payment-rollout', 'Payment Rollout', FiCreditCard],
  ['direct-charge-finance', 'Charge Reconciliation', FiActivity],
  ['tax-mappings', 'Stripe Tax Codes', FiShield]
];

const AdminFinanceSection = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    summary: {},
    transactions: [],
    settings: {}
  });

  const loadFinance = async () => {
    setLoading(true);
    setError('');
    const { data: result, error: requestError } = await supabase.rpc(
      'admin_get_finance_command_center_1793003000000'
    );
    if (requestError) setError(requestError.message);
    else setData(result || { summary: {}, transactions: [], settings: {} });
    setLoading(false);
  };

  React.useEffect(() => {
    loadFinance();
  }, []);

  const exportRows = useMemo(() => data.transactions.map((row) => ({
    order_id: row.order_id,
    buyer: row.buyer,
    seller: row.seller,
    order_status: row.order_status,
    payment_status: row.payment_status,
    subtotal: row.product_subtotal ?? row.subtotal,
    tax: row.tax,
    total_charged: row.total_paid ?? row.total_charged,
    platform_fee: row.platform_commission ?? row.platform_fee,
    stripe_fee: row.stripe_fee,
    refund_amount: row.refund_amount,
    seller_payout: row.seller_payout,
    tax_liable_party: row.tax_liable_party,
    payout_status: row.payout_status,
    risk_flags: (row.risk_flags || []).join('|')
  })), [data.transactions]);

  const saveSettings = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    const form = new FormData(event.currentTarget);
    const values = {
      p_platform_fee_percent: Number(form.get('platform_fee_percent')),
      p_minimum_platform_fee: Number(form.get('minimum_platform_fee')),
      p_fee_calculation_rule: form.get('fee_calculation_rule'),
      p_payment_flow: form.get('payment_flow'),
      p_seller_acceptance_window_minutes: Number(form.get('seller_acceptance_window_minutes')),
      p_refund_rule: form.get('refund_rule'),
      p_tax_collection_mode: form.get('tax_collection_mode'),
      p_payout_delay_days: Number(form.get('payout_delay_days'))
    };
    const { error: saveError } = await supabase.rpc(
      'admin_update_finance_settings_1793003000000',
      values
    );
    if (saveError) setError(saveError.message);
    else await loadFinance();
    setSaving(false);
  };

  const renderTable = (rows = exportRows) => (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1500px]">
        <thead>
          <tr className="bg-gray-50 text-[10px] text-gray-400 uppercase tracking-widest">
            {[
              'Order ID', 'Buyer', 'Seller', 'Order Status', 'Payment Status',
              'Subtotal', 'Tax', 'Total Charged', 'SOHLOCAL Fee', 'Stripe Fee',
              'Refund Amount', 'Seller Payout', 'Tax Liable Party', 'Payout Status', 'Risk Flags'
            ].map((heading) => <th key={heading} className="px-5 py-4 text-left">{heading}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map((row) => (
            <tr key={row.id || row.order_id}>
              <td className="px-5 py-4 font-black text-indigo-600">{row.order_id}</td>
              <td className="px-5 py-4 text-sm font-bold">{row.buyer}</td>
              <td className="px-5 py-4 text-sm">{row.seller}</td>
              <td className="px-5 py-4 text-xs uppercase">{row.order_status}</td>
              <td className="px-5 py-4 text-xs uppercase">{row.payment_status}</td>
              <td className="px-5 py-4 text-sm">{money(row.subtotal)}</td>
              <td className="px-5 py-4 text-sm">{money(row.tax)}</td>
              <td className="px-5 py-4 text-sm font-black">{money(row.total_charged)}</td>
              <td className="px-5 py-4 text-sm text-emerald-600">{money(row.platform_fee)}</td>
              <td className="px-5 py-4 text-sm text-rose-500">{money(row.stripe_fee)}</td>
              <td className="px-5 py-4 text-sm text-amber-600">{money(row.refund_amount)}</td>
              <td className="px-5 py-4 text-sm font-black">{money(row.seller_payout)}</td>
              <td className="px-5 py-4 text-xs uppercase">{row.tax_liable_party}</td>
              <td className="px-5 py-4 text-xs uppercase">{row.payout_status}</td>
              <td className="px-5 py-4">
                <div className="flex flex-wrap gap-1">
                  {(row.risk_flags || []).map((flag) => (
                    <span key={flag} className="rounded-full bg-amber-50 px-2 py-1 text-[9px] font-black text-amber-700">
                      {flag.replaceAll('_', ' ')}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return <div className="rounded-[2.5rem] bg-white p-20 text-center font-black text-indigo-600">Loading finance controls...</div>;
  }

  return (
    <section className="space-y-6">
      {error && <div className="rounded-2xl bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 rounded-[2rem] bg-white p-3 shadow-sm">
        {tabs.map(([id, label, icon]) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center rounded-xl px-4 py-3 text-[10px] font-black uppercase tracking-widest ${
              activeTab === id ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:bg-gray-50'
            }`}
          >
            <SafeIcon icon={icon} className="mr-2" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'tax-mappings' && <AdminStripeTaxMappingsPanel />}
      {activeTab === 'payment-rollout' && <AdminDirectChargeRolloutPanel />}
      {activeTab === 'direct-charge-finance' && <AdminDirectChargeFinancePanel />}

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {metricCards.map(([key, label, icon]) => (
            <div key={key} className="rounded-[2rem] border border-gray-100 bg-white p-6 shadow-sm">
              <SafeIcon icon={icon} className="mb-4 text-xl text-indigo-600" />
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</p>
              <p className="mt-2 text-2xl font-black text-gray-900">
                {key === 'failed_payments' ? data.summary[key] || 0 : money(data.summary[key])}
              </p>
            </div>
          ))}
        </div>
      )}

      {['transactions', 'payouts', 'taxes', 'refunds', 'revenue'].includes(activeTab) && (
        <div className="overflow-hidden rounded-[2.5rem] border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-50 p-8">
            <div>
              <h3 className="text-xl font-black text-gray-900">
                {tabs.find(([id]) => id === activeTab)?.[1]}
              </h3>
              {activeTab === 'taxes' && (
                <p className="mt-1 text-xs font-bold text-amber-600">
                  Review rows with missing GST/HST details, province, tax category, or manual overrides.
                </p>
              )}
            </div>
            <button
              onClick={() => downloadCsv(`sohlocal-${activeTab}.csv`, exportRows)}
              className="flex items-center rounded-xl bg-indigo-600 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white"
            >
              <SafeIcon icon={FiDownload} className="mr-2" />
              Export CSV
            </button>
          </div>
          {renderTable(
            activeTab === 'payouts'
              ? exportRows.filter((row) => row.payout_status === 'pending' || row.payout_status === 'paid')
              : activeTab === 'refunds'
                ? exportRows.filter((row) => row.payment_status === 'refunded' || row.risk_flags.includes('dispute'))
                : activeTab === 'taxes'
                  ? exportRows.filter((row) => row.risk_flags.length)
                  : exportRows
          )}
        </div>
      )}

      {activeTab === 'exports' && (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {[
            ['payments', 'Payments', FiCreditCard],
            ['platform-fees', 'Platform fees', FiDollarSign],
            ['seller-payouts', 'Seller payouts', FiUsers],
            ['taxes', 'Taxes', FiPercent],
            ['refunds', 'Refunds', FiRefreshCw],
            ['monthly-summary', 'Monthly summaries', FiBarChart2]
          ].map(([name, label, icon]) => (
            <button
              key={name}
              onClick={() => downloadCsv(`sohlocal-${name}.csv`, exportRows)}
              className="flex items-center rounded-[2rem] border border-gray-100 bg-white p-7 text-left shadow-sm hover:border-indigo-200"
            >
              <SafeIcon icon={icon} className="mr-4 text-2xl text-indigo-600" />
              <span>
                <strong className="block text-sm font-black text-gray-900">{label}</strong>
                <small className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Download CSV</small>
              </span>
            </button>
          ))}
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          <form onSubmit={saveSettings} className="grid gap-6 rounded-[2.5rem] bg-white p-8 shadow-sm md:grid-cols-2">
            <div>
              <label className="finance-label">Platform fee %</label>
              <input name="platform_fee_percent" defaultValue={data.settings.platform_fee_percent} type="number" step="0.01" className="finance-input" />
            </div>
            <div>
              <label className="finance-label">Minimum platform fee</label>
              <input name="minimum_platform_fee" defaultValue={data.settings.minimum_platform_fee} type="number" step="0.01" className="finance-input" />
            </div>
            <div>
              <label className="finance-label">Fee calculation rules</label>
              <select name="fee_calculation_rule" defaultValue={data.settings.fee_calculation_rule} className="finance-input">
                <option value="subtotal">Product subtotal</option>
                <option value="total">Total charged</option>
              </select>
            </div>
            <div>
              <label className="finance-label">Authorize / capture flow</label>
              <select name="payment_flow" defaultValue={data.settings.payment_flow} className="finance-input">
                <option value="authorize_capture">Authorize, capture on seller acceptance</option>
                <option value="immediate_capture">Capture immediately</option>
              </select>
            </div>
            <div>
              <label className="finance-label">Seller acceptance window, minutes</label>
              <input name="seller_acceptance_window_minutes" defaultValue={data.settings.seller_acceptance_window_minutes} type="number" className="finance-input" />
            </div>
            <div>
              <label className="finance-label">Refund rules</label>
              <select name="refund_rule" defaultValue={data.settings.refund_rule} className="finance-input">
                <option value="full_before_preparation">Full refund before preparation</option>
                <option value="manual_review">Manual review</option>
                <option value="seller_policy">Seller policy</option>
              </select>
            </div>
            <div>
              <label className="finance-label">Tax collection mode</label>
              <select name="tax_collection_mode" defaultValue={data.settings.tax_collection_mode} className="finance-input">
                <option value="platform_collected">Platform collected</option>
                <option value="seller_collected">Seller collected</option>
                <option value="manual">Manual review</option>
              </select>
            </div>
            <div>
              <label className="finance-label">Payout delay, days</label>
              <input name="payout_delay_days" defaultValue={data.settings.payout_delay_days} type="number" className="finance-input" />
            </div>
            <button disabled={saving} className="md:col-span-2 rounded-2xl bg-indigo-600 px-6 py-4 text-[10px] font-black uppercase tracking-widest text-white">
              {saving ? 'Saving...' : 'Save finance settings'}
            </button>
          </form>
        </div>
      )}
    </section>
  );
};

export default AdminFinanceSection;
